import React, { useMemo } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import Chip from '@/components/General/Chip';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SolidButton from '@/components/General/SolidButton';
import MilestoneBadge from '@/components/Milestones/MilestoneBadge';
import { Query } from '@/utils/react-query/Queries';
import { groupMilestonesBySeries, nextMilestoneThreshold } from '@/utils/milestones';

/**
 * Authorized organization milestone achievements + live progress.
 * The server applies the financial gate and excludes hidden donation rows
 * before counts — this component renders what it is given, unfiltered.
 *
 * Each series card is a condensed summary (badge + tier count, next tier,
 * progress) that taps through to the milestone detail page.
 */
const OrganizationMilestoneHistory = ({ organizationID, accountID }) => {
    const router = useRouter();
    const historyQuery = Query.milestones.organizationHistory({ organizationID, accountID });
    const { data, isLoading, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage } = historyQuery;

    const groups = useMemo(
        () => groupMilestonesBySeries({ series: (data?.pages ?? []).flatMap(page => page?.series ?? []) }),
        [data]
    );
    const anyAwards = groups.some(g => g.awards.length > 0);

    if (isLoading) {
        return <View style={styles.center}><ActivityIndicator /></View>;
    }

    if (isError) {
        const denied = error?.milestoneCode === 'MILESTONE_PERMISSION_DENIED';
        return (
            <EmptyListPlaceholder
                noLottie
                spacing={32}
                message={denied ? "You don't have permission to view organization achievements." : "Achievements couldn't be loaded right now. Pull to refresh later."}
            />
        );
    }

    if (!groups.length || !anyAwards) {
        return (
            <EmptyListPlaceholder
                icon="trophy-outline"
                iconColorVariant="gold"
                spacing={32}
                message="Enable milestones and earned achievements will appear here."
            />
        );
    }

    return (
        <View style={styles.list}>
            {groups.filter(g => g.awards.length > 0 || g.progress).map(group => {
                const archived = !!group.deletedAt;
                const tierCount = (group.thresholds?.length || 0) || group.awards.length;
                const current = group.progress?.currentValue != null ? Number(group.progress.currentValue) : null;
                const nextTier = group.progress?.nextThreshold != null
                    ? Number(group.progress.nextThreshold)
                    : nextMilestoneThreshold(group.thresholds ?? [], current ?? 0)?.threshold ?? null;
                const progressPct = current != null && nextTier ? Math.min(100, Math.round((current / nextTier) * 100)) : null;
                const latestEarnedAt = group.latestEarnedAt ? DateTime.fromISO(group.latestEarnedAt).toLocaleString(DateTime.DATE_MED) : null;

                return (
                    <Pressable
                        key={group.milestoneID ?? group.name}
                        style={styles.card}
                        testID={`milestone-achievement-${group.milestoneID}`}
                        accessibilityRole="button"
                        accessibilityLabel={`${group.name} milestone series`}
                        onPress={() => {
                            if (group.milestoneID) router.push(`/settings/organization/milestones/${group.milestoneID}`);
                        }}
                    >
                        <View style={styles.cardHeader}>
                            <MilestoneBadge icon={group.badgeIcon} colorToken={group.badgeColorToken} seriesName={group.name} size="sm" showThreshold={false} earned={group.enabled && !archived} historical={archived} />
                            <View style={styles.cardTitleBlock}>
                                <Text style={styles.cardTitle} numberOfLines={1}>{group.name}</Text>
                                <Text style={styles.cardSubtitle}>
                                    {group.awards.length} tier{group.awards.length === 1 ? '' : 's'} earned
                                    {!!latestEarnedAt && ` · latest ${latestEarnedAt}`}
                                </Text>
                            </View>
                            <Chip colorVariant="gold" icon="ribbon-outline" testID={`milestone-tier-count-${group.milestoneID}`}>×{tierCount}</Chip>
                            {archived
                                ? <Chip colorVariant="warning" icon="archive-outline">Archived</Chip>
                                : !group.enabled && <Chip colorVariant="textSecondary" icon="pause-circle-outline">Paused</Chip>}
                        </View>

                        {current != null && nextTier != null && (
                            <View style={styles.progressBlock}>
                                <View style={styles.progressTrack}>
                                    <View style={styles.progressFill(progressPct ?? 0)} />
                                </View>
                                <Text style={styles.progressLabel}>{current} / {nextTier} toward the next tier</Text>
                            </View>
                        )}
                    </Pressable>
                );
            })}

            {hasNextPage && (
                <SolidButton
                    testID="milestone-history-more"
                    variant="outlined"
                    type="textSecondary"
                    text={isFetchingNextPage ? 'Loading…' : 'Load More'}
                    processing={isFetchingNextPage}
                    onPress={() => fetchNextPage()}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, shadows }) => ({
    center: {
        paddingVertical: spacing.xl,
        alignItems: 'center',
    },
    list: {
        gap: spacing.sm,
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: spacing.md,
        boxShadow: shadows.card,
        gap: spacing.sm,
        _web: {
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease',
            _hover: { boxShadow: shadows.cardHover },
        },
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.md,
    },
    cardTitleBlock: {
        flex: 1,
        minWidth: 0,
    },
    cardTitle: {
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: 15,
    },
    cardSubtitle: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 12,
    },
    progressBlock: {
        gap: spacing.xs,
        marginTop: spacing.xs,
    },
    progressTrack: {
        height: 6,
        borderRadius: 3,
        backgroundColor: colors.surfaceVariant,
        overflow: 'hidden',
    },
    progressFill: (pct) => ({
        width: `${pct}%`,
        height: '100%',
        borderRadius: 3,
        backgroundColor: colors.gold,
    }),
    progressLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 11,
    },
}));

export default OrganizationMilestoneHistory;
